import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef} from "react";
import FeatherIcon from "feather-icons-react";
import {useTranslation} from "react-i18next";
import {useLocation, useParams, useHistory} from "react-router-dom";
import useAxios from "../../../utils/useAxios";
import GridSelectPlayerModal from "./GridSelectPlayerModal";
import TournamentGridMatchesModal from "./TournamentGridMatchesModal";
import Bracket from "./Bracket";
import useTournamentSocket from "../../../hooks/useTournamentSocket";
import useFetchStages from "../../../hooks/useFetchStages";
import EmptyData from "../../EmptyData";
import { openTournamentSettings } from "../../../utils/tournamentSettingsBus";
import { publishTournamentSearchOptions } from "../../../utils/tournamentSearchOptionsBus";
import { fetchTeamRosterMap } from "../../../utils/fetchTeamRosterMap";
import { fetchPlayerNameMap } from "../../../utils/fetchPlayerNameMap";
import { getOrdinalSuffix } from "../../../constants";
import PageLoader from "../../../components/PageLoader";
import BracketClassic from "./BracketClassic";
import useViewMode from "../../../hooks/useViewMode";
import StageSelector from "../components/StageSelector";
import IconDropdown from "../../../components/IconDropdown";
import { useTheme } from "../../../contexts/ThemeContext";
import { useIsWideViewport } from "../../../hooks/useIsWideViewport";


export default function TournamentGrids({ tournament, is_authorized, hasGuestToken, stageControl, sheetTargetRef, viewMode: controlledViewMode, onViewModeChange, searchChips = [] }) {

    const { t, i18n } = useTranslation();
    const { compactTable } = useTheme();
    const { id } = useParams();
    const location = useLocation();
    const history = useHistory();
    const api = useAxios();

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const currentTab = searchParams.get('tab');

    const updateSearchParams = (params) => {
        history.replace({
            pathname: location.pathname,
            search: params.toString()
        });
    };

    const [draws, setDraws] = useState([]);
    const [isDrawsLoading, setIsDrawsLoading] = useState(true);

    const [participants, setParticipants] = useState([]);
    const participantReqRef = useRef(0);
    // Same out-of-order-response guard as participantReqRef, for fetchDraws:
    // add/move/delete on a draw slot each fire their own resync fetch with
    // no cancellation between them, so a slower/older response landing
    // last after repeated rapid actions could clobber newer state with
    // stale occupancy data - see fetchDraws below.
    const drawsReqRef = useRef(0);

    // Scrolls the first highlighted slot into view - queried off the DOM
    // (searchStateClass's own ".search-match", set per-slot by
    // Bracket.js/BracketClassic.js) rather than tracked through props, since
    // both view modes and however many draws are rendered here at once all
    // share this one effect. Same technique as TournamentGroups.js's own
    // version of this effect.
    const lastScrolledMatchRef = useRef(null);
    useEffect(() => {
        if (searchChips.length === 0) {
            lastScrolledMatchRef.current = null;
            return;
        }
        const match = document.querySelector("#brackets-block .search-match");
        if (match && match !== lastScrolledMatchRef.current) {
            lastScrolledMatchRef.current = match;
            match.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [searchChips, draws]);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    // A candidate picked from the still-open picker panel (see
    // GridSelectPlayerModal.js's own hasTarget branch) rather than from a
    // specific "+"-targeted slot - same "Drop here"/crosshair treatment as
    // each Bracket/BracketClassic instance's own pickedSlot move-mechanic,
    // just placing a NEW participant instead of moving an already-seated
    // one. Lives here (not local to one Bracket/BracketClassic instance
    // like pickedSlot) because the modal that sets it and every bracket
    // instance that can receive it are all siblings, children of this
    // component - "All brackets" view renders one instance per draw, and
    // the candidate must be placeable into any of them, not just the one
    // it happened to be picked "near".
    const [pickedCandidate, setPickedCandidate] = useState(null);
    // Same "loading-dots on the slot itself" feedback TournamentGroups.js's
    // own loadingCells already gives its cells while an add-participant
    // request is in flight - without it, a slow connection made a slot tap
    // look like nothing happened at all. Keyed the same way Bracket.js/
    // BracketClassic.js already key their own (unrelated, drag-move-only)
    // loadingSlots state: `${matchId}_${side}`. Lives here rather than
    // inside one Bracket/BracketClassic instance because the request that
    // sets it (GridSelectPlayerModal.js's handleSelect, or this component's
    // own handlePlaceCandidateInMatch below) isn't scoped to one instance.
    const [loadingCells, setLoadingCells] = useState({});
    // Wide viewports dock the participant-picker panel and the matches sheet
    // on the same right-edge slot (see ParticipantPickerPanel/BottomSheetPanel)
    // - only one can occupy it at a time, so the matches sheet below is
    // unmounted for as long as the picker is open there.
    const isWide = useIsWideViewport();

    const [selectedMatchId, setSelectedMatchId] = useState(null);
    // Bumped (not just selectedMatchId, which may already equal the clicked
    // match) on every match click so the matches sheet always (re)expands,
    // even for a re-click on the same match.
    const [expandSignal, setExpandSignal] = useState(0);
    // The matches sheet only exists in the DOM while actually open now (see
    // BottomSheetPanel.jsx's own comment on why the old permanent peek was
    // removed) - sheetTargetRef survives this component being torn down and
    // rebuilt (the Draw tab swapping its whole Groups<->Grids subtree when
    // the selected stage crosses from a group stage to a bracket stage or
    // back), so a sheet the user had open stays open across that swap
    // instead of a fresh mount always defaulting closed. "full"/"peek" are
    // the same two sentinel strings the sheet's own drag-to-resize logic
    // used to write here before it lost its PEEK resting state - reused as
    // open/closed instead of introducing a third value just for this.
    const [sheetOpen, setSheetOpen] = useState(() => sheetTargetRef?.current === "full");
    useEffect(() => {
        if (sheetTargetRef) sheetTargetRef.current = sheetOpen ? "full" : "peek";
    }, [sheetOpen, sheetTargetRef]);
    // Closes the participant picker (if open) same as its own onClose does -
    // both panels dock on the same right-edge slot, and since the picker no
    // longer auto-closes after a pick (it stays open across several adds in
    // a row), it would otherwise keep hogging that slot forever, leaving no
    // way back to the matches sheet short of the picker's own tiny X.
    // Clicking any match now always wins the slot back for it instead.
    // useCallback - passed down into Bracket/BracketClassic (both now
    // React.memo'd) as a prop; every setter here is stable, so an empty
    // deps array is correct.
    const expandMatch = useCallback((matchId) => {
        setSelectedMatchId(matchId);
        setExpandSignal(n => n + 1);
        setSheetOpen(true);
        setModalOpen(false);
        setPickedCandidate(null);
    }, []);
    const closeMatchesSheet = () => setSheetOpen(false);

    // The Classic/Pro toggle itself always renders here (next to the
    // place-tab row, see the "grid-tabs-wrap" li below), for both standalone
    // and merged (stageControl set, the Жеребкування flow via TournamentDraw
    // Stages) use. When controlled, though, the *state* it flips is owned by
    // that parent (controlledViewMode/onViewModeChange - its own
    // "grids_view_mode" useViewMode instance, so TournamentDrawStages' own
    // group-format sibling stays in sync too) - this own hook still runs
    // (rules of hooks) but its value/setter are only used standalone.
    const ownViewMode = useViewMode("grids_view_mode", "classic");
    const viewMode = stageControl ? controlledViewMode : ownViewMode.viewMode;
    const toggleViewMode = stageControl ? onViewModeChange : ownViewMode.toggleViewMode;

    const ownFetchStages = useFetchStages(
        api, `/api/v1.0/tournaments/${id}/draw-stage/`
    );
    const { stages, activeStage, setActiveStage, isLoading: isStagesLoading } = stageControl || ownFetchStages;

    useTournamentSocket(tournament?.id, (payload) => {
        if (payload?.wake_up) {
            // Routed through fetchDraws (not a standalone api.get) so it
            // shares drawsReqRef's race guard - see the identical fix/
            // reasoning for TournamentGroups.js's own wake_up handler.
            if (activeStage) {
                fetchDraws(activeStage);
                if (tournament?.can_edit && is_authorized) {
                    fetchParticipants(activeStage);
                }
            }
            return;
        }

        if (activeStage && payload?.updated_matches) {
            updateMatches(payload.updated_matches, false);
        }
    });

    useEffect(() => {
        if (!stageControl) ownFetchStages.fetchStages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const urlStage = searchParams.get('stage');
    useEffect(() => {
        // In controlled mode, the parent (TournamentDrawStages.js) owns which
        // stage is active and its own ?stage= sync - running this effect too
        // would fight it over setActiveStage.
        if (stageControl) return;
        if (currentTab && currentTab !== 'brackets') return;
        if (stages.length > 0) {
            // String(...) on both sides - `urlStage` comes out of
            // URLSearchParams as a string no matter what, while `s.id` is
            // whatever type the API returned (a number, typically). A bare
            // `===` between those two never matched, so this treated every
            // valid ?stage= as invalid and silently forced it back to
            // stages[0] on every rerun - including reruns triggered by
            // totally unrelated location.search changes (a player-profile
            // click, a preview-frame resize closing a docked panel, etc.),
            // which is what made an already-selected stage keep "snapping
            // back" to the first one.
            const matchedStage = stages.find(s => String(s.id) === String(urlStage));
            if (!matchedStage) {
                const newParams = new URLSearchParams(location.search);
                newParams.set('stage', stages[0].id);
                newParams.delete('gridTab');
                updateSearchParams(newParams);
                if (activeStage !== stages[0].id) setActiveStage(stages[0].id);
            } else if (activeStage !== matchedStage.id) {
                setActiveStage(matchedStage.id);
            }
        } else if (!isStagesLoading && stages.length === 0) {
            if (activeStage) setActiveStage(null);
            if (urlStage || searchParams.get('gridTab')) {
                const newParams = new URLSearchParams(location.search);
                newParams.delete('stage');
                newParams.delete('gridTab');
                updateSearchParams(newParams);
            }
        }
    }, [stages, urlStage, location.search, currentTab, isStagesLoading, activeStage]);

    useEffect(() => {
        if (activeStage && tournament?.can_edit) {
            fetchParticipants(activeStage);
        }
    }, [activeStage, tournament?.can_edit, is_authorized]);

    useEffect(() => {
        if (isStagesLoading) return;

        if (stages.length === 0) {
            setDraws([]);
            setParticipants([]);
            setIsDrawsLoading(false);
            return;
        }

        // Stages exist but activeStage hasn't been resolved from the URL yet
        // (the stage-resolving effect above calls setActiveStage, which this
        // effect - running in the same commit - doesn't see until the next
        // render) - stay in the loading state instead of flashing "no draws"
        // for that one render before activeStage lands.
        if (!activeStage) return;

        // Routed through fetchDraws (not a standalone api.get) so this
        // stage-mount fetch shares drawsReqRef's race guard too - see the
        // identical fix/reasoning in TournamentGroups.js.
        let isMounted = true;
        setIsDrawsLoading(true);

        fetchDraws(activeStage).finally(() => {
            if (isMounted) setIsDrawsLoading(false);
        });

        return () => { isMounted = false; };
    }, [activeStage, stages.length, isStagesLoading, id]);

    // useCallback (here and on the handlers below that pass down into
    // Bracket/BracketClassic as props) - those two are now React.memo'd, so
    // a plain function const recreated fresh on every TournamentGrids render
    // would silently defeat that: memo's shallow prop comparison sees a
    // "changed" function prop every time regardless of whether anything
    // relevant actually changed, forcing every mounted instance (every
    // draw's whole bracket table) to re-render anyway.
    const fetchParticipants = useCallback(async (stageId) => {
        const isTournamentAdmin = tournament?.can_edit && is_authorized;
        if (!isTournamentAdmin) return;

        participantReqRef.current += 1;
        const currentReq = participantReqRef.current;

        try {
            const res = await api.get(`/api/v1.0/draw-stage/${stageId}/participants/?limit=100`);
            if (participantReqRef.current === currentReq) {
                setParticipants(res.data.results || []);
            }
        } catch (e) {
            console.error(e);
        }
    }, [tournament?.can_edit, is_authorized, api]);

    // rollback for optimistic long-press/drag moves in Bracket/BracketClassic:
    // if the API call behind a move fails, re-fetch the authoritative state
    // instead of leaving the optimistically-swapped participants in place
    const fetchDraws = useCallback(async (stageId) => {
        if (!stageId) return;
        drawsReqRef.current += 1;
        const currentReq = drawsReqRef.current;
        try {
            const res = await api.get(`/api/v1.0/tournaments/${id}/draw-stage/${stageId}/draws/`);
            if (drawsReqRef.current !== currentReq) return;
            setDraws(res.data.results);
        } catch (e) {
            console.error(e);
        }
    }, [api, id]);

    // Moved up from its old spot (right before onDeleteParticipant) - both
    // handleAddToMatch and handlePlaceCandidateInMatch below now depend on
    // this as a useCallback, and a useCallback's own dependency array is
    // evaluated immediately (unlike the callback body, which only runs
    // later) - referencing this before its `const` initializes throws
    // "Cannot access 'updateMatches' before initialization" the moment this
    // component first renders. Same class of bug already hit once this
    // session with Bracket.js/BracketClassic.js's own handleTouchStart/
    // openParticipantMenu ordering.
    const updateMatches = useCallback((updatedMatches, fetch_pl = true) => {
        if (!updatedMatches?.length) return;
        const map = Object.fromEntries(updatedMatches.map(m => [m.id, m]));

        setDraws(prev => prev.map(draw => {
            // skip draws with nothing in this batch of updates - otherwise every
            // draw (including ones untouched by this move) gets a brand new
            // `matches` array reference, which invalidates every useMemo/useCallback
            // keyed on it in every mounted Bracket/BracketClassic instance and forces
            // a full re-render of each - noticeable as sluggishness/hangs when
            // several long-press moves fire in quick succession
            if (!draw.matches.some(m => map[m.id])) return draw;

            return {
                ...draw,
                matches: draw.matches.map(match => {
                const upd = map[match.id];
                if (!upd) return match;

                if (upd.sets && upd.sets.some(s => s.number === undefined) && match.sets && match.sets.length > upd.sets.length) {
                    delete upd.sets;
                }

                let mergedSets = match.sets;
                if (upd.sets) {
                    const setsMap = {};
                    (match.sets || []).forEach((s, i) => {
                        if (s) {
                            const num = s.number !== undefined ? s.number : i + 1;
                            setsMap[num] = s;
                        }
                    });
                    upd.sets.forEach((s, i) => {
                        if (s) {
                            const num = s.number !== undefined ? s.number : i + 1;
                            setsMap[num] = { ...(setsMap[num] || {}), ...s };
                        }
                    });
                    const res = Object.values(setsMap).sort((a, b) => a.number - b.number);
                    mergedSets = res.length > 0 ? res : upd.sets;
                }

                return {
                    ...match,
                    ...upd,
                    sets: mergedSets
                };
            })
            };
        }));

        if (fetch_pl) {
            fetchParticipants(activeStage);
        }
    }, [fetchParticipants, activeStage]);

    const handleAddToMatch = useCallback((participant = null, isBye = false, matchId, side) => {
        if (!participant && !isBye) return;

        setParticipants(prev =>
            participant ? prev.filter(p =>
                tournament?.grade === 1 ? p.player_id !== participant.player_id : p.team_id !== participant.team_id
            ) : prev
        );

        setDraws(prev => prev.map(draw => ({
            ...draw,
            matches: draw.matches.map(match => {
                if (match.id !== matchId) return match;

                if (isBye) return { ...match, [`x${side}`]: true };

                return {
                    ...match,
                    [`x${side}`]: null,
                    ...(tournament?.grade === 1
                        ? { [`player_${side}_name`]: participant }
                        : tournament?.grade === 2
                            ? side === 1
                                ? { player_1_name: participant.players[0], player_3_name: participant.players[1] }
                                : { player_2_name: participant.players[0], player_4_name: participant.players[1] }
                            : {
                                [`team_${side}_name`]: participant.name,
                                [`team_${side}_number`]: participant.number,
                                [`team_${side}`]: true,
                            })
                };
            })
        })));

        const match = draws.flatMap(d => d.matches).find(m => m.id === matchId);
        if (!match) return;

        const occupied = tournament?.grade === 1
            ? (side === 1 ? match.player_1_name : match.player_2_name)
            : tournament?.grade === 2
                ? (side === 1 ? match.player_1_name || match.player_3_name : match.player_2_name || match.player_4_name)
                : (side === 1 ? match.team_1_name : match.team_2_name);

        if (occupied) fetchParticipants(activeStage);
    }, [tournament?.grade, draws, activeStage, fetchParticipants]);

    // Tapping the already-picked candidate again cancels the pick, same
    // "tap source again to cancel" idea each Bracket/BracketClassic
    // instance's own handleCellTap already uses for pickedSlot.
    const getCandidateId = (c) => c?.isBye ? "__bye__" : (c?.participant?.player_id || c?.participant?.id || c?.participant?.team_id);
    const handlePickCandidate = (candidate) => {
        setPickedCandidate(prev => (prev && getCandidateId(prev) === getCandidateId(candidate)) ? null : candidate);
    };

    // The reverse flow: a candidate (or Bye) was picked from the still-open
    // panel (no specific slot targeted yet - see pickedCandidate above) and
    // the user just tapped an empty round-1 slot to place it there. Unlike
    // handleAddToMatch (optimistic-update only, called from inside the
    // modal's own handleSelect after ITS OWN PATCH resolves), this is the
    // destination click happening in a sibling component - the modal isn't
    // on the call stack, so this does the request itself too. Payload
    // shape mirrors GridSelectPlayerModal.js's own handleSelect exactly.
    const handlePlaceCandidateInMatch = useCallback(async (matchId, side) => {
        if (!pickedCandidate) return;
        const { participant, isBye } = pickedCandidate;
        const cellKey = `${matchId}_${side}`;
        setLoadingCells(prev => ({ ...prev, [cellKey]: true }));
        try {
            let payload;
            if (isBye) {
                payload = side === 1 ? { x1: true } : { x2: true };
            } else if (tournament?.grade === 1) {
                payload = side === 1 ? { player_1: participant.player_id } : { player_2: participant.player_id };
            } else if (tournament?.grade === 2) {
                payload = side === 1
                    ? { team_1: participant.team_id, player_1: participant.players[0].id, player_3: participant.players[1].id }
                    : { team_2: participant.team_id, player_2: participant.players[0].id, player_4: participant.players[1].id };
            } else if (tournament?.grade === 3) {
                payload = side === 1 ? { team_1: participant.team_id } : { team_2: participant.team_id };
            }

            const response = await api.patch(`/api/v1.0/tournaments/${id}/draws/matches/${matchId}/`, payload);
            try {
                if (response?.data?.updated_matches) updateMatches(response.data.updated_matches, true);
            } catch (e) {
                console.error("Failed to merge server match update:", e);
            }
            try {
                handleAddToMatch(participant, isBye, matchId, side);
            } catch (e) {
                console.error("Failed to apply local match update:", e);
            }
            setPickedCandidate(null);
        } catch (e) {
            console.error("Failed to place participant:", e);
        } finally {
            setLoadingCells(prev => {
                const next = { ...prev };
                delete next[cellKey];
                return next;
            });
        }
    }, [pickedCandidate, tournament?.grade, api, id, updateMatches, handleAddToMatch]);

    const onDeleteParticipant = useCallback((response) => {
        if (response?.data?.updated_matches) {
            updateMatches(response.data.updated_matches, true);
        } else {
            fetchDraws(activeStage);
        }
        fetchParticipants(activeStage);
    }, [updateMatches, fetchDraws, activeStage, fetchParticipants]);

    // Stable (useCallback) versions of what used to be inline arrow props on
    // Bracket/BracketClassic below - both are now React.memo'd, and a fresh
    // inline `() => ...` on every render defeats that the same way a plain
    // function const would (see fetchParticipants' own comment above).
    const handleCancelCandidate = useCallback(() => setPickedCandidate(null), []);
    // A failed move can leave the picker's participant pool stale relative to
    // the grid (e.g. the PATCH that placed the participant in the new slot
    // went through but the DELETE clearing the old one didn't, or vice versa,
    // if the connection drops mid-move) - refetch both, not just the grid, so
    // "add participant" reflects who's actually still available afterward.
    const handleMoveFailed = useCallback(() => {
        fetchDraws(activeStage);
        fetchParticipants(activeStage);
    }, [fetchDraws, fetchParticipants, activeStage]);

    // ИЗМЕНЕНИЕ 3: Вынесли логику калькуляции табов в отдельную функцию
    const generateTabsForStage = (stageDraws) => {
        const visible = stageDraws.filter(draw => {
            const firstRoundMatches = draw.matches.filter(m => m.round_number === 1);
            if (firstRoundMatches.length === 0) return true;
            const isAllByes = firstRoundMatches.every(m => m.x1 && m.x2);
            return !isAllByes;
        });

        const stageTabs = [];
        if (visible.length > 0) {
            const mainDraw = visible.reduce((prev, current) => (prev.number_players > current.number_players) ? prev : current, visible[0]);

            stageTabs.push({
                id: 'main',
                title: `${mainDraw.number} ${t('Place')}`,
                draws: [mainDraw]
            });

            const consolationDraws = visible.filter(d => d.id !== mainDraw.id);
            const isContained = (d1, d2) => {
                if (d1.id === d2.id) return false;
                return d1.number >= d2.number && (d1.number + d1.number_players) <= (d2.number + d2.number_players);
            };

            const topLevelDraws = consolationDraws.filter(d => !consolationDraws.some(other => isContained(d, other)));
            topLevelDraws.sort((a, b) => a.number - b.number);

            let currentTabDraws = [];
            let currentTabStart = null;
            let currentTabEnd = null;

            topLevelDraws.forEach(tDraw => {
                const start = tDraw.number;
                const end = tDraw.number + tDraw.number_players - 1;
                const subDraws = consolationDraws.filter(d => isContained(d, tDraw) || d.id === tDraw.id);
                subDraws.sort((a, b) => a.number - b.number);

                if (currentTabDraws.length === 0) {
                    currentTabStart = start;
                    currentTabEnd = end;
                    currentTabDraws = [...subDraws];
                } else {
                    if (end <= mainDraw.number + 7 && currentTabStart === mainDraw.number + 2) {
                        currentTabEnd = end;
                        currentTabDraws = currentTabDraws.concat(subDraws);
                    } else {
                        stageTabs.push({
                            id: `tab_${currentTabStart}_${currentTabEnd}`,
                            title: `${currentTabStart}-${currentTabEnd} ${t('Place')}`,
                            draws: currentTabDraws
                        });
                        currentTabStart = start;
                        currentTabEnd = end;
                        currentTabDraws = [...subDraws];
                    }
                }
            });

            if (currentTabDraws.length > 0) {
                stageTabs.push({
                    id: `tab_${currentTabStart}_${currentTabEnd}`,
                    title: `${currentTabStart}-${currentTabEnd} ${t('Place')}`,
                    draws: currentTabDraws
                });
            }

            stageTabs.sort((a, b) => {
                const matchA = a.title.match(/\d+/);
                const matchB = b.title.match(/\d+/);
                const numA = matchA ? parseInt(matchA[0], 10) : 0;
                const numB = matchB ? parseInt(matchB[0], 10) : 0;
                return numA - numB;
            });
        }
        return { visible, stageTabs };
    };

    const { visible: visibleDraws, stageTabs: tabs } = useMemo(() => {
        return generateTabsForStage(draws);
    }, [draws, t]);

    // Lazily reads the URL synchronously (not null) - the effect below is
    // the one that validates/corrects this against the actual generated
    // `tabs` once draws finish loading, but that only runs post-render.
    // Starting from null meant the very first paint always showed "Всі
    // сітки" un-highlighted (activeTabId !== '__all__' yet), then a tick
    // later flipped to active once the effect ran - a visible inactive-
    // then-green flash. Same zero-delay trick TournamentGroups' own
    // activeGroupId already gets for free there (its "all" sentinel IS its
    // initial state, null).
    const [activeTabId, setActiveTabId] = useState(() => new URLSearchParams(location.search).get('gridTab') || '__all__');
    const urlTab = searchParams.get('gridTab');

    // Debounced, not immediate: `tabs` gets recomputed on every draws refetch,
    // including the ones useTournamentSocket's wake_up handler above fires
    // while a stage is live - and a placement bracket right at the tail end
    // can flicker in/out of generateTabsForStage's all-byes visibility filter
    // from one fetch to the next while its matches are still being resolved
    // in real time. Correcting the URL on every such blip made gridTab (and
    // the visible tab bar) snap back to "Всі сітки" and then right back to
    // the tail tab, over and over, for as long as the stage stayed live -
    // visible as the address bar "jumping" on its own. Waiting a beat means
    // a tab that reappears on the next fetch (the common live-data case)
    // never touches the URL at all; only a selection that's still genuinely
    // missing once things settle gets corrected.
    const gridTabCorrectionTimerRef = useRef(null);
    // Tracks whether gridTab has already been resolved to a real value for
    // the CURRENT activeStage - reset to false the moment activeStage
    // changes (below), flipped true the first time this effect resolves a
    // valid tab for it (either branch). First resolution for a stage uses
    // 0ms (see delayMs below): switching Стадія1 (groups) -> Стадія2
    // (brackets) for the first time has no gridTab in the URL yet (groups
    // use a different param), so isValidUrlTab below is false and this
    // effect would otherwise sit on the full 1000ms debounce before
    // snapping to '__all__' - drawsToRender meanwhile falls through
    // activeTab's own `tabs.find(...) || tabs[0]` fallback (see that const
    // above) to just the main bracket, so the real visible symptom was a
    // full second of nothing, then just the main bracket alone, then every
    // other bracket landing ~1s later once this fired - reported as
    // "сетки грузятся одна за одной" specifically on a groups->brackets
    // stage switch (the outer Інформація/Учасники/... tab click has its own
    // separate gridsReady fix above, already covering unrelated ground).
    // Later re-corrections on an otherwise-already-settled view (the
    // scenario this debounce was actually written for - a live placement
    // bracket flickering in/out of generateTabsForStage's all-byes filter
    // while its matches are still resolving) keep the full 1000ms, same as
    // before.
    const stageTabResolvedRef = useRef(false);
    useEffect(() => {
        stageTabResolvedRef.current = false;
    }, [activeStage]);

    useEffect(() => {
        if (gridTabCorrectionTimerRef.current) {
            clearTimeout(gridTabCorrectionTimerRef.current);
            gridTabCorrectionTimerRef.current = null;
        }

        if (currentTab && currentTab !== 'brackets' && currentTab !== 'draw-stages') return;

        if (tabs.length > 0) {
            const isValidUrlTab = urlTab === '__all__' || tabs.some(t => t.id === urlTab);
            if (!isValidUrlTab) {
                // Defaults to "Всі сітки" (every bracket at once), not just the
                // main one - same "show everything until the user narrows it
                // down" default the group-tabs row's own "Всі групи" already
                // uses.
                const delayMs = stageTabResolvedRef.current ? 1000 : 0;
                gridTabCorrectionTimerRef.current = setTimeout(() => {
                    gridTabCorrectionTimerRef.current = null;
                    stageTabResolvedRef.current = true;
                    const newParams = new URLSearchParams(location.search);
                    newParams.set('gridTab', '__all__');
                    updateSearchParams(newParams);
                    setActiveTabId('__all__');
                }, delayMs);
            } else {
                stageTabResolvedRef.current = true;
                if (activeTabId !== urlTab) {
                    setActiveTabId(urlTab);
                }
            }
        } else {
            setActiveTabId(null);
        }

        return () => {
            if (gridTabCorrectionTimerRef.current) {
                clearTimeout(gridTabCorrectionTimerRef.current);
                gridTabCorrectionTimerRef.current = null;
            }
        };
    }, [tabs, urlTab, location.search, currentTab]);

    // activeTabId is only synced to a real tab by the effect above, which
    // fires a render tick after tabs first becomes non-empty (e.g. right
    // when draws finish loading) - falling back to tabs[0] for that one
    // frame avoids flashing the "no brackets" empty state before it catches up.
    const activeTab = activeTabId === '__all__' ? null : (tabs.find(t => t.id === activeTabId) || tabs[0]);
    const drawsToRender = activeTabId === '__all__' ? tabs.flatMap(t => t.draws) : (activeTab ? activeTab.draws : []);
    const showSkeleton = isStagesLoading || isDrawsLoading;

    // Reached via a direct load, all N bracket tables mount together with
    // nothing else competing for the main thread, so the browser's own
    // first paint already lands as one shot. Reached by clicking the tab
    // strip instead (TournamentSwipeTabs' own 0.22s opacity fade-in on the
    // incoming tab), that same first paint now has to compete with the
    // fade's own per-frame compositing work, which visibly staggers heavy
    // brackets in one at a time instead of all at once - reported as
    // "сетки грузятся одна за одной" specifically on tab-click navigation.
    // Mounting the real bracket tables invisibly first (see the wrapper
    // below), then flipping them visible only after the browser has had two
    // rAF ticks to actually paint that hidden layer, turns the reveal into
    // one already-rasterized, compositor-only opacity flip regardless of
    // whatever's animating around it.
    // Only reset on the actual loading -> loaded transition (showSkeleton
    // going true -> false), NOT on every place/stage-tab or Classic<->Pro
    // switch - those remount a different set of already-fetched brackets
    // with nothing competing for the main thread (no swipe-fade involved),
    // so gating them the same way just hid already-visible content for two
    // frames on every tap, reported as a new flicker ("стала мигать при
    // загрузке второй сетки").
    const [gridsReady, setGridsReady] = useState(!showSkeleton);
    const wasSkeletonRef = useRef(showSkeleton);
    useLayoutEffect(() => {
        if (wasSkeletonRef.current && !showSkeleton) {
            setGridsReady(false);
        }
        wasSkeletonRef.current = showSkeleton;
    }, [showSkeleton]);
    useEffect(() => {
        if (gridsReady || drawsToRender.length === 0) return;
        // setTimeout, not requestAnimationFrame - this component also
        // mounts inside ScreenshotButton's capture iframe, which is
        // `visibility:hidden` by design (so it never flashes on screen
        // during a capture). Chromium suspends rAF for hidden/off-screen
        // iframes, which left gridsReady stuck false in there forever - the
        // wrapper below stayed hidden through the whole capture, producing
        // a blank image (ScreenshotButton's own waitForLoadingToFinish
        // faithfully waited on this component's PageLoader, which itself
        // never unmounted because gridsReady never flipped). setTimeout
        // keeps firing regardless of the iframe's own visibility - only a
        // whole background TAB gets throttled (1s+), not a hidden iframe
        // inside the active one - so this stays reliable there while still
        // giving the browser a couple of frames' worth of time to paint the
        // hidden layer before it's revealed.
        const timer = setTimeout(() => setGridsReady(true), 32);
        return () => clearTimeout(timer);
    }, [gridsReady, drawsToRender.length]);

    const occupiedParticipants = useMemo(() => {
        const list = [];
        (draws || []).forEach(d => {
            (d.matches || []).forEach(m => {
                if (m.round_number === 1) {
                    if (m.player_1) list.push(m.player_1);
                    if (m.player_2) list.push(m.player_2);
                    if (m.player_3) list.push(m.player_3);
                    if (m.player_4) list.push(m.player_4);
                    if (m.team_1) list.push(m.team_1);
                    if (m.team_2) list.push(m.team_2);
                    if (m.player_1_name?.id) list.push(m.player_1_name.id);
                    if (m.player_2_name?.id) list.push(m.player_2_name.id);
                    if (m.player_3_name?.id) list.push(m.player_3_name.id);
                    if (m.player_4_name?.id) list.push(m.player_4_name.id);
                }
            });
        });
        return list;
    }, [draws]);

    // Team (grade 3) bracket matches only carry team_1_name/team_2_name, not
    // the team's own roster - see fetchTeamRosterMap.js's own comment.
    // Fetched once per tournament so a "player" search chip can still match
    // a team slot by any of its players, not just the team's own name.
    const [teamRoster, setTeamRoster] = useState(new Map());
    useEffect(() => {
        if (tournament?.grade !== 3 || !tournament?.player_quantity) return;
        let cancelled = false;
        fetchTeamRosterMap(api, id, tournament.player_quantity).then(map => {
            if (!cancelled) setTeamRoster(map);
        });
        return () => { cancelled = true; };
    }, [api, id, tournament?.grade, tournament?.player_quantity]);

    // Singles/doubles (grade 1/2): match.player_X_name only carries each
    // player's first_name truncated to an initial (see fetchPlayerNameMap.js's
    // own comment, same quirk already fixed for groups in TournamentGroups.js)
    // - fetched once per tournament so a full-first-name search chip still
    // matches a bracket slot.
    const [playerNames, setPlayerNames] = useState(new Map());
    useEffect(() => {
        if (tournament?.grade === 3 || !tournament?.player_quantity) return;
        let cancelled = false;
        fetchPlayerNameMap(api, id, tournament.player_quantity).then(map => {
            if (!cancelled) setPlayerNames(map);
        });
        return () => { cancelled = true; };
    }, [api, id, tournament?.grade, tournament?.player_quantity]);

    // Bracket ("draw") + player-name chip options for the global search box -
    // see tournamentSearchOptionsBus's own comment. Mirrors ScheduleTable's
    // own searchOptions computation (stage/group/draw/player scanned off
    // whatever's actually loaded), scoped to just draws/players here since
    // this tab only ever shows brackets.
    useEffect(() => {
        const players = new Map();
        visibleDraws.forEach(d => {
            (d.matches || []).forEach(m => {
                [1, 2, 3, 4].forEach(num => {
                    const rawP = m[`player_${num}_name`];
                    const pid = m[`player_${num}`] || rawP?.id;
                    const p = (rawP && pid) ? (playerNames.get(pid) || rawP) : rawP;
                    if (p?.last_name) {
                        const full = `${p.last_name} ${p.first_name || ''}`.trim();
                        players.set(full, full);
                    }
                });
                [1, 2].forEach(side => {
                    const teamName = m[`team_${side}_name`];
                    if (teamName) {
                        players.set(teamName, teamName);
                        (teamRoster.get(teamName) || []).forEach(pl => {
                            if (pl?.last_name) {
                                const full = `${pl.last_name} ${pl.first_name || ''}`.trim();
                                players.set(full, full);
                            }
                        });
                    }
                });
            });
        });
        const options = [];
        if (visibleDraws.length > 0) {
            options.push({
                label: t("Brackets"),
                options: visibleDraws.map(d => ({
                    value: `d_${d.number}`,
                    label: `${d.number}${getOrdinalSuffix(d.number)} ${t("Place")}`,
                    type: 'draw',
                    realValue: d.number
                }))
            });
        }
        if (players.size > 0) {
            options.push({ label: t("Players"), options: Array.from(players.values()).map(n => ({ value: `p_${n}`, label: n, type: 'player' })) });
        }
        publishTournamentSearchOptions(options);
    }, [visibleDraws, teamRoster, playerNames, t]);

    return (
        <>
            {pickedCandidate && (
                <div
                    onClick={() => setPickedCandidate(null)}
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#111827",
                        color: "white",
                        padding: "10px 20px",
                        borderRadius: "20px",
                        zIndex: 100,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        userSelect: "none"
                    }}
                >
                    <span style={{ fontSize: "14px", fontWeight: "500", whiteSpace: "nowrap" }}>{t("Tap destination cell")}</span>
                    <FeatherIcon icon="x" size={16} />
                </div>
            )}
            {/* .admin-wrapper's own overflow-x:auto clips any negative-margin
                edge-to-edge bleed further down the tree (.bracket-table/
                .bracket-classic-table's own compact-mode margin/width, see
                index.css) at this wrapper's own padding box instead of
                letting it reach the true page edge - same class of bug
                already fixed for Участники's team rows (TournamentPlayers.
                js). Dropped entirely in compact mode; the bracket table
                itself already scrolls horizontally on its own when wider
                than the viewport, so nothing here actually needs this
                wrapper's own scroll/card chrome once compact. */}
            <div className={compactTable ? "" : `admin-wrapper ${(stages?.length > 0 || tabs?.length > 0) ? "admin-wrapper-tournament-stages" : ""}`}>
                    {(stages?.length > 0 || tabs?.length > 0) && (
                    <ul className="tabs tournament-stages" style={{ minHeight: "25px" }}>
                                {!stageControl && stages?.length > 0 && (
                                    <StageSelector
                                        stages={stages}
                                        activeStage={activeStage}
                                        onStageChange={(stageId) => {
                                            const newParams = new URLSearchParams(location.search);
                                            newParams.set('stage', stageId);
                                            newParams.delete('gridTab');
                                            updateSearchParams(newParams);
                                            setActiveStage(stageId);
                                        }}
                                        chevron={false}
                                    />
                                )}

                                {/* Place tabs ("1 Місце"/"3-8 Місце"/...) can add up to more
                                    than the row's width on tournaments with several
                                    placement brackets - flex:0 0 auto (see .grid-tab-item)
                                    never shrinks them, so without a scroll lane of their
                                    own they used to just overflow the whole row and push
                                    the Classic/Pro toggle off past the visible edge.
                                    .grid-tabs-wrap shrinks (flex:1 1 auto/min-width:0, same
                                    trick as StageTabsScroll's own .stage-tabs-wrap) and
                                    .grid-tabs-scroll scrolls horizontally inside it, so the
                                    tabs scroll right up to the toggle instead. */}
                                {tabs?.length > 0 && (
                                    <li className="grid-tabs-wrap">
                                        {/* 17px inset before the first tab, matching the
                                            group-tabs row's own "Всі групи" - see
                                            .grid-tabs-scroll's own comment (index.css) for why
                                            that's hardcoded there rather than via the shared
                                            .tournament-subtoolbar-inset class. */}
                                        <ul className="grid-tabs-scroll">
                                            {tabs.length > 1 && (
                                                <li
                                                    className={`grid-tab-item ${activeTabId === '__all__' ? 'active' : ''}`}
                                                    onClick={() => {
                                                        const newParams = new URLSearchParams(location.search);
                                                        newParams.set('gridTab', '__all__');
                                                        updateSearchParams(newParams);
                                                        setActiveTabId('__all__');
                                                    }}
                                                >
                                                    {t('All brackets')}
                                                </li>
                                            )}

                                            {tabs.map(tab => (
                                                <li
                                                    key={tab.id}
                                                    // tabs.length===1: the "All brackets" toggle above is hidden then,
                                                    // but activeTabId still defaults to '__all__' (not this tab's id) -
                                                    // without this the lone tab rendered unstyled/inactive even though
                                                    // it's the only (and effectively selected) one.
                                                    className={`grid-tab-item ${(activeTabId === tab.id || tabs.length === 1) ? 'active' : ''}`}
                                                    onClick={() => {
                                                        const newParams = new URLSearchParams(location.search);
                                                        newParams.set('gridTab', tab.id);
                                                        updateSearchParams(newParams);
                                                        setActiveTabId(tab.id);
                                                    }}
                                                >
                                                    {tab.title}
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                )}

                                <IconDropdown
                                    activeId={viewMode}
                                    onChange={toggleViewMode}
                                    className="action-button matches-btn"
                                    options={[
                                        { id: "classic", icon: "menu", label: "Classic" },
                                        { id: "pro", icon: "grid", label: "Pro" }
                                    ]}
                                    right={true}
                                />
                    </ul>
                    )}

                <div id={visibleDraws?.length > 0 || showSkeleton ? "brackets-block" : ""} style={{ position: "relative" }}>
                    {showSkeleton ? (
                        <PageLoader />
                    ) : (
                        drawsToRender.length > 0 ? (
                            <>
                            {!gridsReady && <PageLoader />}
                            <div style={gridsReady ? undefined : { visibility: "hidden", position: "absolute", top: 0, left: 0, right: 0 }}>
                            {drawsToRender.map((draw) => (
                                <div key={draw.id}>
                                    {viewMode === "classic" ? (
                                            <BracketClassic
                                                draw={draw}
                                                tournamentGrade={tournament?.grade}
                                                setModalOpen={setModalOpen}
                                                setModalData={setModalData}
                                                pickerTarget={isWide && modalOpen ? modalData : null}
                                                pickedCandidate={pickedCandidate}
                                                onPlaceCandidate={handlePlaceCandidateInMatch}
                                                onCancelCandidate={handleCancelCandidate}
                                                loadingCells={loadingCells}
                                                mainGridNumber={visibleDraws[0]?.id === draw.id ? draw.number : null}
                                                onDeleteParticipant={onDeleteParticipant}
                                                expandMatch={expandMatch}
                                                is_authorized={is_authorized}
                                                canEdit={tournament?.can_edit}
                                                updateMatches={updateMatches}
                                                onMoveFailed={handleMoveFailed}
                                                searchChips={searchChips}
                                                teamRoster={teamRoster}
                                                playerNames={playerNames}
                                            />
                                        ) :
                                        <Bracket
                                            draw={draw}
                                            tournamentGrade={tournament?.grade}
                                            setModalOpen={setModalOpen}
                                            setModalData={setModalData}
                                            pickerTarget={isWide && modalOpen ? modalData : null}
                                            pickedCandidate={pickedCandidate}
                                            onPlaceCandidate={handlePlaceCandidateInMatch}
                                            onCancelCandidate={handleCancelCandidate}
                                            loadingCells={loadingCells}
                                            mainGridNumber={visibleDraws[0]?.id === draw.id ? draw.number : null}
                                            onDeleteParticipant={onDeleteParticipant}
                                            expandMatch={expandMatch}
                                            is_authorized={is_authorized}
                                            canEdit={tournament?.can_edit}
                                            updateMatches={updateMatches}
                                            onMoveFailed={handleMoveFailed}
                                            searchChips={searchChips}
                                                teamRoster={teamRoster}
                                                playerNames={playerNames}
                                        />
                                    }
                                </div>
                            ))}
                            </div>
                            </>
                        ) : (
                            <EmptyData
                                title="No brackets created"
                                text="The tournament brackets has not been generated yet."
                                actionLabel="Go to settings"
                                onAction={is_authorized ? () => openTournamentSettings("draw-stages") : undefined}
                            />
                        )
                    )}
                </div>
            </div>

            <TournamentGridMatchesModal
                // Not gated on draws.length>0 anymore - sheetOpen can only
                // ever turn true from an actual match click (which requires
                // draws to already exist) or from sheetTargetRef inheriting
                // "already open" across TournamentDrawStages' Groups<->Grids
                // swap (a real remount - see its own comment). In that swap
                // case `draws` here starts at [] until the new stage's own
                // fetchDraws resolves - gating on its length made the sheet
                // vanish for that whole round-trip and then snap back open
                // the instant data landed, instead of just staying open
                // (empty list area very briefly) across the switch like the
                // user's remembered "open" state implies it should.
                isOpen={sheetOpen && !(isWide && modalOpen)}
                onClose={closeMatchesSheet}
                expandSignal={expandSignal}
                draws={drawsToRender}
                tournamentId={tournament?.id}
                tournamentGrade={tournament?.grade}
                selectedMatchId={selectedMatchId}
                setSelectedMatchId={setSelectedMatchId}
                stages={stages}
                activeStage={activeStage}
                setActiveStage={setActiveStage}
                updateMatches={updateMatches}
                is_authorized={is_authorized || hasGuestToken}
                searchChips={searchChips}
                teamRoster={teamRoster}
                playerNames={playerNames}
            />

            <GridSelectPlayerModal
                {...modalData}
                tournamentId={id}
                occupiedParticipants={occupiedParticipants}
                tournamentGrade={tournament?.grade}
                rating={tournament?.rating}
                participants={participants}
                onAddToMatch={handleAddToMatch}
                updateMatches={updateMatches}
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    // Closing used to leave a still-unplaced pick stuck on -
                    // the panel was the only place to cancel it (tap the same
                    // candidate again), so closing it lost that and every
                    // click on an occupied slot underneath kept silently
                    // no-oping (see Bracket.js/BracketClassic.js's own
                    // onCancelCandidate). Closing the panel now cancels the
                    // pick too, same as clicking through to an occupied slot
                    // already does.
                    setPickedCandidate(null);
                }}
                // Clears the just-consumed target (not the panel itself,
                // which stays open on wide) - the FIRST pick after a "+"
                // click still fills that exact slot instantly; every pick
                // after that has no target, which is what flips the
                // modal's own candidate-click into "just pick, wait for a
                // slot tap" instead of posting immediately (see
                // pickedCandidate above).
                onSuccess={() => setModalData(null)}
                pickedCandidate={pickedCandidate}
                onPickCandidate={handlePickCandidate}
                setLoadingCells={setLoadingCells}
            />
        </>
    );
}
